import { nanoid } from '@reduxjs/toolkit'
import {
  type CreateCustomIntegrationPayload,
  type CustomIntegration,
  type CustomIntegrationAuthConfig,
  type CustomIntegrationAuthConfigInput,
  type CustomIntegrationAuthPatch,
  type CustomIntegrationAuthType,
  type IdentityConfig,
  type IdentityRequestMethod,
  type UpdateCustomIntegrationPayload,
} from '../../../models/CustomIntegration'

export const NAME_MAX_LENGTH = 255
export const DESCRIPTION_MAX_LENGTH = 1024
export const DEFAULT_API_KEY_HEADER_NAME = 'X-Api-Key'

export const AUTH_TYPES_WITH_DETAILS: CustomIntegrationAuthType[] = ['oauth2', 'oauth2Dcr', 'apiKey']

export interface DetailsFormData {
  name: string
  endpointUrl: string
  description: string
  imageUrl: string
}

export interface HeaderRow {
  id: string
  name: string
  value: string
}

export interface IdentityFormData {
  url: string
  method: IdentityRequestMethod
  accountIdentifierPath: string
  headers: HeaderRow[]
}

export interface OAuth2FormData {
  clientId: string
  clientSecret: string
  authorizationUrl: string
  tokenUrl: string
  useBasicAuth: boolean
  pkceEnabled: boolean
  scopes: string[]
  identity: IdentityFormData
}

export interface DcrFormData {
  identity: IdentityFormData
}

export interface ApiKeyFormData {
  headerName: string
}

export interface AuthFormState {
  oauth2: OAuth2FormData
  oauth2Dcr: DcrFormData
  apiKey: ApiKeyFormData
}

export const createHeaderRow = (name = '', value = ''): HeaderRow => ({ id: nanoid(), name, value })

export const createIdentityFormData = (): IdentityFormData => ({ url: '', method: 'GET', accountIdentifierPath: '', headers: [createHeaderRow()] })

export const createOAuth2FormData = (): OAuth2FormData => ({
  clientId: '',
  clientSecret: '',
  authorizationUrl: '',
  tokenUrl: '',
  useBasicAuth: false,
  pkceEnabled: true,
  scopes: [],
  identity: createIdentityFormData(),
})

export const createAuthFormState = (): AuthFormState => ({
  oauth2: createOAuth2FormData(),
  oauth2Dcr: { identity: createIdentityFormData() },
  apiKey: { headerName: DEFAULT_API_KEY_HEADER_NAME },
})

export const INITIAL_DETAILS: DetailsFormData = { name: '', endpointUrl: '', description: '', imageUrl: '' }

export const isHttpsUrl = (value: string): boolean => URL.canParse(value) && new URL(value).protocol === 'https:'

export const hasAuthDetailsStep = (authType: CustomIntegrationAuthType | null): boolean => authType !== null && AUTH_TYPES_WITH_DETAILS.includes(authType)

export const isDetailsValid = (details: DetailsFormData): boolean => {
  const name = details.name.trim()
  const imageUrl = details.imageUrl.trim()
  return (
    name.length > 0 &&
    name.length <= NAME_MAX_LENGTH &&
    isHttpsUrl(details.endpointUrl.trim()) &&
    details.description.length <= DESCRIPTION_MAX_LENGTH &&
    (imageUrl === '' || isHttpsUrl(imageUrl))
  )
}

const isHeaderRowBlank = (row: HeaderRow): boolean => row.name.trim() === '' && row.value.trim() === ''

export const isIdentityValid = (identity: IdentityFormData): boolean => {
  const url = identity.url.trim()
  if (url === '') {
    return true
  }
  return isHttpsUrl(url) && identity.headers.every((row) => isHeaderRowBlank(row) || row.name.trim() !== '')
}

export const isOAuth2Valid = (data: OAuth2FormData): boolean =>
  data.clientId.trim() !== '' &&
  data.clientSecret.trim() !== '' &&
  isHttpsUrl(data.authorizationUrl.trim()) &&
  isHttpsUrl(data.tokenUrl.trim()) &&
  isIdentityValid(data.identity)

export const isAuthDetailsValid = (authType: CustomIntegrationAuthType, state: AuthFormState): boolean => {
  if (authType === 'oauth2') {
    return isOAuth2Valid(state.oauth2)
  }
  if (authType === 'oauth2Dcr') {
    return isIdentityValid(state.oauth2Dcr.identity)
  }
  if (authType === 'apiKey') {
    return state.apiKey.headerName.trim() !== ''
  }
  return true
}

const buildIdentity = (identity: IdentityFormData): IdentityConfig | null => {
  const url = identity.url.trim()
  if (url === '') {
    return null
  }
  const headers = identity.headers
    .filter((row) => !isHeaderRowBlank(row))
    .reduce<Record<string, string>>((acc, row) => ({ ...acc, [row.name.trim()]: row.value }), {})
  const accountIdentifierPath = identity.accountIdentifierPath.trim()
  return { url, method: identity.method, accountIdentifierPath: accountIdentifierPath === '' ? null : accountIdentifierPath, headers }
}

export const buildAuthConfig = (authType: CustomIntegrationAuthType, state: AuthFormState): CustomIntegrationAuthConfigInput => {
  if (authType === 'oauth2') {
    const data = state.oauth2
    return {
      type: 'oauth2',
      authorizationUrl: data.authorizationUrl.trim(),
      tokenUrl: data.tokenUrl.trim(),
      clientId: data.clientId.trim(),
      clientSecret: data.clientSecret,
      scopes: data.scopes,
      pkceEnabled: data.pkceEnabled,
      tokenEndpointAuthMethod: data.useBasicAuth ? 'client_secret_basic' : 'client_secret_post',
      identity: buildIdentity(data.identity),
    }
  }
  if (authType === 'oauth2Dcr') {
    return { type: 'oauth2Dcr', scopes: [], identity: buildIdentity(state.oauth2Dcr.identity) }
  }
  if (authType === 'apiKey') {
    return { type: 'apiKey', headerName: state.apiKey.headerName.trim() }
  }
  return { type: authType }
}

export const buildCreatePayload = (details: DetailsFormData, authType: CustomIntegrationAuthType, state: AuthFormState): CreateCustomIntegrationPayload => {
  const description = details.description.trim()
  const imageUrl = details.imageUrl.trim()
  return {
    name: details.name.trim(),
    endpointUrl: details.endpointUrl.trim(),
    description: description === '' ? null : description,
    imageUrl: imageUrl === '' ? null : imageUrl,
    auth: buildAuthConfig(authType, state),
    kind: 'mcp',
  }
}

const identityToFormData = (identity: IdentityConfig | null | undefined): IdentityFormData => {
  if (!identity) {
    return createIdentityFormData()
  }
  const headers = Object.entries(identity.headers ?? {}).map(([name, value]) => createHeaderRow(name, value))
  return {
    url: identity.url,
    method: identity.method,
    accountIdentifierPath: identity.accountIdentifierPath ?? '',
    headers: headers.length ? headers : [createHeaderRow()],
  }
}

export const detailsFromCustomIntegration = (customIntegration: CustomIntegration): DetailsFormData => ({
  name: customIntegration.name,
  endpointUrl: customIntegration.endpointUrl,
  description: customIntegration.description ?? '',
  imageUrl: customIntegration.imageUrl ?? '',
})

export const authFormStateFromConfig = (auth: CustomIntegrationAuthConfig): AuthFormState => {
  const state = createAuthFormState()
  if (auth.type === 'oauth2') {
    return {
      ...state,
      oauth2: {
        clientId: auth.clientId,
        clientSecret: '',
        authorizationUrl: auth.authorizationUrl,
        tokenUrl: auth.tokenUrl,
        useBasicAuth: auth.tokenEndpointAuthMethod === 'client_secret_basic',
        pkceEnabled: auth.pkceEnabled,
        scopes: auth.scopes,
        identity: identityToFormData(auth.identity),
      },
    }
  }
  if (auth.type === 'oauth2Dcr') {
    return { ...state, oauth2Dcr: { identity: identityToFormData(auth.identity) } }
  }
  if (auth.type === 'apiKey') {
    return { ...state, apiKey: { headerName: auth.headerName } }
  }
  return state
}

export const isSameFormValue = <T>(a: T, b: T): boolean => JSON.stringify(a) === JSON.stringify(b)

export const isAuthEditValid = (
  authType: CustomIntegrationAuthType,
  state: AuthFormState,
  initialAuthType: CustomIntegrationAuthType,
  initialClientId: string,
): boolean => {
  if (authType !== 'oauth2') {
    return isAuthDetailsValid(authType, state)
  }
  const data = state.oauth2
  const secretRequired = initialAuthType !== 'oauth2' || data.clientId.trim() !== initialClientId
  return (
    data.clientId.trim() !== '' &&
    (!secretRequired || data.clientSecret.trim() !== '') &&
    isHttpsUrl(data.authorizationUrl.trim()) &&
    isHttpsUrl(data.tokenUrl.trim()) &&
    isIdentityValid(data.identity)
  )
}

export const buildAuthPatch = (authType: CustomIntegrationAuthType, state: AuthFormState): CustomIntegrationAuthPatch => {
  const config = buildAuthConfig(authType, state)
  if (config.type === 'oauth2') {
    const { clientSecret, ...rest } = config
    return clientSecret ? { ...rest, clientSecret } : rest
  }
  if (config.type === 'oauth2Dcr') {
    return { type: 'oauth2Dcr', identity: config.identity }
  }
  return config
}

export const buildDetailsPatch = (details: DetailsFormData): UpdateCustomIntegrationPayload => {
  const description = details.description.trim()
  const imageUrl = details.imageUrl.trim()
  return {
    name: details.name.trim(),
    endpointUrl: details.endpointUrl.trim(),
    description: description === '' ? null : description,
    imageUrl: imageUrl === '' ? null : imageUrl,
  }
}
